import React, { CSSProperties, SelectHTMLAttributes } from 'react'
import { SelectValue } from 'antd/es/select'
import { TransferItem } from 'antd/es/transfer'
import { MultiSelect, Select } from 'components/Select'
import { FieldValidator, useField } from 'formik'

import { ErrorMessage } from './ErrorMessage'

type SizeType = 'small' | 'middle' | 'large'

interface SizeTypeProps {
  sm: SizeType
  md: SizeType
  lg: SizeType
}

const SizeTypeParams: SizeTypeProps = {
  sm: 'small',
  md: 'middle',
  lg: 'large',
}
// TODO: don't allow line breaks in interfaces or objects
export interface FormSelectProps
  extends Omit<
    SelectHTMLAttributes<HTMLSelectElement>,
    'size' | 'defaultValue' | 'onFocus' | 'onBlur' | 'onChange'
  > {
  name: string
  children: React.ReactNode | React.ReactNode[]
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onBlur?: React.FocusEventHandler<HTMLElement>
  onChange?: (value: SelectValue) => void
  validate?: FieldValidator
  showSearch?: boolean
  placeholder?: string
  showDropdown?: boolean
  allowClear?: boolean
  filterOption?: (inputValue: string, option: TransferItem) => boolean
  optionFilterProp?: string
  dropdownStyle?: CSSProperties
  labelInValue?: boolean
  defaultValue?: SelectValue
  bordered?: boolean
  fullWidth?: boolean
  showLoading?: boolean
  size?: keyof typeof SizeTypeParams
  'data-test'?: string
  hideError?: boolean
  dropdownRender?(menu: React.ReactNode): React.ReactNode
}

export const FormSelect: React.FC<FormSelectProps> = ({
  children,
  onChange,
  validate,
  hideError,
  ...props
}: FormSelectProps) => {
  const [{ value, ...field }, _meta, helper] = useField({ name: props.name, validate })

  return (
    <>
      <Select
        {...props}
        {...field}
        id={props.id ?? props.name}
        value={value}
        onChange={(selectValue: SelectValue): void => {
          helper.setValue(selectValue)

          if (onChange) onChange(selectValue)
        }}
      >
        {children}
      </Select>
      {!hideError && <ErrorMessage name={props.name} />}
    </>
  )
}

export const FormMultiSelect: React.FC<FormSelectProps> = ({
  children,
  onChange,
  validate,
  ...props
}: FormSelectProps) => {
  const [{ value, ...field }, _meta, helper] = useField({ name: props.name, validate })

  return (
    <>
      <MultiSelect
        {...props}
        {...field}
        value={value}
        onChange={(selectValue: SelectValue): void => {
          helper.setTouched(true)
          helper.setValue(selectValue)

          if (onChange) onChange(selectValue)
        }}
      >
        {children}
      </MultiSelect>
    </>
  )
}
